import { isDeckId, isWorkbookId } from 'pc-types';

/**
 * Module-scoped seam between the tour driver and the chat runtime/composer.
 * The composer instances and the chat runtime live behind memo()'d trees and
 * keep-alive editor mounts, so the tour talks to them through this registry
 * instead of threading props. Everything here is inert when no tour is
 * active: the driver is the only writer, and every consumer no-ops on empty
 * state.
 */

export type TourDemoReplayId = 'sheet' | 'deck' | 'comments';

export interface TourComposerHandle {
  /** Replaces the composer value outright (prefill must be idempotent). */
  setText: (text: string) => void;
  focus: () => void;
  /** Same code path as clicking Send (no-op when the composer can't send). */
  submit: () => void;
  getElement: () => HTMLElement | null;
}

export type TourChatEvent =
  | {
      type: 'message-sent';
      documentId: string;
      demoReplayId?: TourDemoReplayId;
    }
  | { type: 'run-completed'; documentId: string }
  /** The run for this document ended in an error or was cancelled. */
  | { type: 'run-failed'; documentId: string };

const composers = new Set<TourComposerHandle>();
const listeners = new Set<(event: TourChatEvent) => void>();
let pendingDemoReplayId: TourDemoReplayId | null = null;
let pendingDemoReplayDocumentId: string | null = null;

export function registerTourComposer(handle: TourComposerHandle): () => void {
  composers.add(handle);
  return () => composers.delete(handle);
}

export function isTourAnchorVisible(element: HTMLElement | null): boolean {
  if (!element || !element.isConnected) return false;
  // Keep-alive editor mounts hide via visibility/content-visibility (never
  // display:none), so offsetParent alone cannot tell hidden instances apart.
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({
      checkVisibilityCSS: true,
      contentVisibilityAuto: true,
    });
  }
  const rect = element.getBoundingClientRect();
  return element.offsetParent !== null && rect.width > 0 && rect.height > 0;
}

/** Populate the visible chat composer. Returns false when none is visible. */
export function prefillTourComposer(text: string): boolean {
  for (const handle of composers) {
    if (!isTourAnchorVisible(handle.getElement())) continue;
    handle.setText(text);
    handle.focus();
    return true;
  }
  return false;
}

/** Send the visible chat composer's message. Returns false when none is
 * visible. */
export function sendTourComposer(): boolean {
  for (const handle of composers) {
    if (!isTourAnchorVisible(handle.getElement())) continue;
    handle.submit();
    return true;
  }
  return false;
}

// Chat reveal, registered by the editors: switches their sidebar to the Chat
// tab. Prompt stages need it because the sidebar remembers its last-open tab
// (e.g. Comments), which keeps the composer hidden — and prefill only ever
// targets a visible composer.
const chatRevealers = new Set<() => void>();

export function registerTourChatReveal(reveal: () => void): () => void {
  chatRevealers.add(reveal);
  return () => chatRevealers.delete(reveal);
}

export function revealTourChatPanel(): void {
  for (const reveal of chatRevealers) reveal();
}

// Deck slide navigation, registered by the ACTIVE deck editor tab: selects a
// slide (0-based index into deck order) and forces comment pins visible. The
// commentsIntro beat uses it to land on the slide the demo comments were
// planted on. Returns whether any editor handled the call so the driver can
// retry while the editor mounts.
const deckSlideNavigators = new Set<(index: number) => void>();

export function registerTourDeckGoToSlide(
  goToSlide: (index: number) => void
): () => void {
  deckSlideNavigators.add(goToSlide);
  return () => deckSlideNavigators.delete(goToSlide);
}

export function tourDeckGoToSlide(index: number): boolean {
  for (const goToSlide of deckSlideNavigators) goToSlide(index);
  return deckSlideNavigators.size > 0;
}

// Dictation start, notified by use-dictation: the voiceIntro beat advances
// when the user actually starts dictating. Inert outside the tour — the
// driver is the only subscriber, so with no tour active this is a no-op.
const dictationStartListeners = new Set<() => void>();

const dictationEndListeners = new Set<() => void>();

export function subscribeTourDictationEnd(listener: () => void): () => void {
  dictationEndListeners.add(listener);
  return () => dictationEndListeners.delete(listener);
}

export function notifyTourDictationEnd(): void {
  for (const listener of dictationEndListeners) listener();
}

export function subscribeTourDictationStart(listener: () => void): () => void {
  dictationStartListeners.add(listener);
  return () => dictationStartListeners.delete(listener);
}

export function notifyTourDictationStart(): void {
  for (const listener of dictationStartListeners) listener();
}

// Demo-space tree expansion: the dataRoom beat spotlights the space's
// subtree, which reads as an empty ring when the node is collapsed. The
// `wanted` flag covers rows that MOUNT while the beat is active (navigation
// remounts the tree) so they start expanded instead of growing post-mount;
// the registry covers rows already mounted.
const demoTreeExpanders = new Set<() => void>();
let demoTreeExpandWanted = false;

export function registerTourDemoTreeExpand(expand: () => void): () => void {
  demoTreeExpanders.add(expand);
  return () => demoTreeExpanders.delete(expand);
}

export function expandTourDemoTree(): void {
  for (const expand of demoTreeExpanders) expand();
}

export function setTourDemoTreeExpandWanted(wanted: boolean): void {
  demoTreeExpandWanted = wanted;
}

export function isTourDemoTreeExpandWanted(): boolean {
  return demoTreeExpandWanted;
}

// Composer lock, set by the driver across a prompt stage and its replay run:
// the prefilled prompt must go out unedited, and the replay must not be
// stoppable. External store so the memo()'d composer subscribes directly.
let composerLocked = false;
const composerLockListeners = new Set<() => void>();

export function setTourComposerLock(locked: boolean): void {
  if (composerLocked === locked) return;
  composerLocked = locked;
  for (const listener of composerLockListeners) listener();
}

export function isTourComposerLocked(): boolean {
  return composerLocked;
}

export function subscribeTourComposerLock(listener: () => void): () => void {
  composerLockListeners.add(listener);
  return () => composerLockListeners.delete(listener);
}

// Canvas lock, set by the driver across the commentsIntro beat: the slide
// content is view-only (no selection/drag/text editing, no edit shortcuts)
// while the comment pins stay fully interactive. Same external-store shape
// as the composer lock so the deck editor subscribes directly.
let canvasLocked = false;
const canvasLockListeners = new Set<() => void>();

export function setTourCanvasLock(locked: boolean): void {
  if (canvasLocked === locked) return;
  canvasLocked = locked;
  for (const listener of canvasLockListeners) listener();
}

export function isTourCanvasLocked(): boolean {
  return canvasLocked;
}

export function subscribeTourCanvasLock(listener: () => void): () => void {
  canvasLockListeners.add(listener);
  return () => canvasLockListeners.delete(listener);
}

/**
 * Set while the tour sits on a prompt stage; the chat runtime consults it
 * when building the agent.message payload. Scoped to a document kind so a
 * send from an unrelated open editor can never pick it up.
 */
export function setTourPendingDemoReplayId(
  id: TourDemoReplayId | null,
  documentId?: string | null
): void {
  pendingDemoReplayId = id;
  // The document binding outlives the id: replay stages carry no pending id
  // (nothing more to send) but still need terminal events filtered to the
  // document the beat targeted.
  pendingDemoReplayDocumentId = documentId ?? null;
}

/** The document the current prompt stage was entered on; terminal run events
 * for OTHER documents (keep-alive editors) must not drive the tour. */
export function getTourExpectedDocumentId(): string | null {
  return pendingDemoReplayDocumentId;
}

export function getTourDemoReplayId(
  documentId: string
): TourDemoReplayId | undefined {
  // Bound to the exact document the prompt stage targeted: an id-kind match
  // alone would let a send from an unrelated same-kind editor consume the
  // replay against the wrong document.
  if (
    pendingDemoReplayDocumentId &&
    documentId !== pendingDemoReplayDocumentId
  ) {
    return undefined;
  }
  if (pendingDemoReplayId === 'sheet' && isWorkbookId(documentId)) {
    return 'sheet';
  }
  if (pendingDemoReplayId === 'deck' && isDeckId(documentId)) return 'deck';
  if (pendingDemoReplayId === 'comments' && isDeckId(documentId)) {
    return 'comments';
  }
  return undefined;
}

export function subscribeTourChatEvents(
  listener: (event: TourChatEvent) => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitTourChatEvent(event: TourChatEvent): void {
  for (const listener of listeners) listener(event);
}
